import gsap from "gsap";
import flatpickr from "flatpickr";
import "flatpickr/dist/flatpickr.min.css";
import klaviyo from "../helpers/klaviyo";
import { trackNewsletterSignup } from "../helpers/analytics";

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
const E164_PHONE_REGEX = /^\+[1-9]\d{7,14}$/;

function getPhoneValidationMessage(phone) {
  const trimmed = (phone || "").trim();
  if (!trimmed) return "";

  const digits = trimmed.replace(/\D/g, "");
  if (trimmed.startsWith("+")) {
    return E164_PHONE_REGEX.test(trimmed)
      ? ""
      : "Use 10 digits or +15005550006.";
  }

  if (digits.length === 10) return "";
  if (digits.length === 11 && digits.startsWith("1")) return "";

  return "Enter a valid US phone number.";
}

function showFormError(successMessageEl, errorMessageEl, message) {
  errorMessageEl.innerText = message;
  gsap.to(successMessageEl, { autoAlpha: 0 });
  gsap.to(errorMessageEl, { autoAlpha: 1 });
}

function buildCustomHeader(instance) {
  const container = instance.calendarContainer;
  if (container.querySelector(".dob-header")) return;

  const header = document.createElement("div");
  header.className = "dob-header";

  const monthSelect = document.createElement("select");
  monthSelect.className = "dob-header__month";
  monthSelect.setAttribute("aria-label", "Select month");
  MONTHS.forEach((name, i) => {
    const opt = document.createElement("option");
    opt.value = i;
    opt.textContent = name;
    monthSelect.appendChild(opt);
  });
  monthSelect.value = instance.currentMonth;
  monthSelect.addEventListener("change", () => {
    instance.changeMonth(Number(monthSelect.value), false);
  });

  const currentYear = new Date().getFullYear();
  const yearSelect = document.createElement("select");
  yearSelect.className = "dob-header__year";
  yearSelect.setAttribute("aria-label", "Select year");
  for (let y = currentYear; y >= 1900; y--) {
    const opt = document.createElement("option");
    opt.value = y;
    opt.textContent = y;
    yearSelect.appendChild(opt);
  }
  yearSelect.value = instance.currentYear;
  yearSelect.addEventListener("change", () => {
    instance.changeYear(Number(yearSelect.value));
  });

  header.appendChild(monthSelect);
  header.appendChild(yearSelect);

  const monthsEl = container.querySelector(".flatpickr-months");
  if (monthsEl) monthsEl.style.display = "none";
  container.insertBefore(header, container.firstChild);
}

function syncCustomHeader(instance) {
  const container = instance.calendarContainer;
  const monthSelect = container?.querySelector(".dob-header__month");
  const yearSelect = container?.querySelector(".dob-header__year");
  if (monthSelect) monthSelect.value = instance.currentMonth;
  if (yearSelect) yearSelect.value = instance.currentYear;
}

class NewsletterForm {
  constructor(el) {
    this.container = el;
    this.klaviyoForm();
  }

  setAnimations(els) {
    els.forEach((el) => {
      el.classList.remove("hidden");
      gsap.set(el, {
        autoAlpha: 0,
      });
    });
  }

  async klaviyoForm() {
    const newsletterEls = document.querySelectorAll("[data-newsletter-form]");

    const data = {};

    newsletterEls.forEach(async (formWrapper) => {
      const formEl = formWrapper.querySelector("form");
      const successMessageEl = formWrapper.querySelector(
        "[data-newsletter-success]",
      );
      const errorMessageEl = formWrapper.querySelector(
        "[data-newsletter-error]",
      );
      const listId = formEl.getAttribute("data-list-id");
      const emailEL = formEl.querySelector("[data-newsletter-email]");
      const firstNameEl = formEl.querySelector('[name="firstName"]');
      const inputsEls = formEl.querySelectorAll("input");
      const submitBtn = formEl.querySelector(".newsletter__submit");
      let titleEl = formWrapper.parentElement.querySelector("h3");
      if (!titleEl) {
        titleEl = formWrapper.parentElement.querySelector(".newsletter-title");
      }
      const isSimplified = !firstNameEl;
      const dobEl = formEl.querySelector("[data-newsletter-dob]");
      const dobFieldWrapper = dobEl?.closest(".klaviyo-newsletter__field--dob");
      const phoneEl = formEl.querySelector("[data-newsletter-phone]");
      const formContainer =
        formWrapper.closest("[data-newsletter-container]") ||
        formWrapper.parentElement;
      const ageMessageEl = formContainer.querySelector(
        "[data-newsletter-age-message]",
      );
      const disclaimerEl = formContainer.querySelector(".privacy-text");

      if (dobEl && !dobEl._flatpickr) {
        flatpickr(dobEl, {
          dateFormat: "m.d.Y",
          allowInput: true,
          disableMobile: true,
          maxDate: "today",
          static: true,
          nextArrow: '<span aria-hidden="true">+</span>',
          prevArrow: '<span aria-hidden="true">-</span>',
          onReady: (_selectedDates, _dateStr, instance) => {
            instance.calendarContainer.classList.add("newsletter-datepicker");
            buildCustomHeader(instance);
            syncCustomHeader(instance);
          },
          onOpen: (_selectedDates, _dateStr, instance) => {
            syncCustomHeader(instance);
          },
          onMonthChange: (_selectedDates, _dateStr, instance) => {
            syncCustomHeader(instance);
          },
          onYearChange: (_selectedDates, _dateStr, instance) => {
            syncCustomHeader(instance);
          },
        });

        dobEl.addEventListener("input", () => {
          let val = dobEl.value.replace(/[^\d]/g, "");
          if (val.length > 8) val = val.slice(0, 8);
          const parts = [];
          if (val.length > 0) parts.push(val.slice(0, 2));
          if (val.length > 2) parts.push(val.slice(2, 4));
          if (val.length > 4) parts.push(val.slice(4, 8));
          dobEl.value = parts.join(".");
        });
      }

      if (phoneEl) {
        const togglePhoneFields = () => {
          const hasPhone = (phoneEl.value || "").trim().length > 0;
          if (dobFieldWrapper)
            dobFieldWrapper.classList.toggle("hidden", !hasPhone);
          if (ageMessageEl) ageMessageEl.classList.toggle("hidden", !hasPhone);
          if (disclaimerEl) {
            disclaimerEl.classList.toggle("mt-4", hasPhone);
            disclaimerEl.classList.toggle("mt-16", !hasPhone);
          }
        };
        phoneEl.addEventListener("input", togglePhoneFields);
        phoneEl.addEventListener("blur", togglePhoneFields);
        togglePhoneFields();
      }

      this.setAnimations([successMessageEl, errorMessageEl]);

      formWrapper.addEventListener("submit", async (e) => {
        e.preventDefault();

        inputsEls.forEach((input) => {
          data[input.name] = input.value;
        });

        if (!data.email) {
          showFormError(
            successMessageEl,
            errorMessageEl,
            "Please enter an email",
          );
          return;
        }

        if (!klaviyo.validateEmail(data.email)) {
          showFormError(
            successMessageEl,
            errorMessageEl,
            "Please enter a valid email",
          );
          return;
        }

        if (!isSimplified && !data.firstName) {
          showFormError(
            successMessageEl,
            errorMessageEl,
            "Please enter your first name",
          );
          return;
        }

        let dateOfBirth = null;
        const rawPhone = (data.phone || "").trim();
        const phoneEntered = rawPhone.length > 0;
        const phoneValidationMessage = getPhoneValidationMessage(rawPhone);

        if (phoneValidationMessage) {
          showFormError(
            successMessageEl,
            errorMessageEl,
            phoneValidationMessage,
          );
          return;
        }

        if (!isSimplified && phoneEntered) {
          if (!data.dateOfBirth || !data.dateOfBirth.trim()) {
            showFormError(
              successMessageEl,
              errorMessageEl,
              "Please confirm your date of birth to receive mobile notifications.",
            );
            return;
          }
          const dobMatch = data.dateOfBirth.match(
            /^(\d{2})\.(\d{2})\.(\d{4})$/,
          );
          if (!dobMatch) {
            showFormError(
              successMessageEl,
              errorMessageEl,
              "Please enter a valid date of birth (MM.DD.YYYY).",
            );
            return;
          }
          const [, mm, dd, yyyy] = dobMatch;
          const dobDate = new Date(`${yyyy}-${mm}-${dd}`);
          if (isNaN(dobDate.getTime()) || dobDate > new Date()) {
            showFormError(
              successMessageEl,
              errorMessageEl,
              "Please enter a valid date of birth.",
            );
            return;
          }
          dateOfBirth = `${yyyy}-${mm}-${dd}`;
        } else if (
          !isSimplified &&
          data.dateOfBirth &&
          data.dateOfBirth.trim()
        ) {
          const dobMatch = data.dateOfBirth.match(
            /^(\d{2})\.(\d{2})\.(\d{4})$/,
          );
          if (dobMatch) {
            const [, mm, dd, yyyy] = dobMatch;
            const dobDate = new Date(`${yyyy}-${mm}-${dd}`);
            if (!isNaN(dobDate.getTime()) && dobDate <= new Date()) {
              dateOfBirth = `${yyyy}-${mm}-${dd}`;
            }
          }
        }

        const response = await klaviyo.subscribe(
          data.email,
          data.firstName,
          data.lastName,
          data.tag,
          listId,
          { phone: rawPhone, dateOfBirth },
        );

        if (response.success) {
          const signupLocation =
            formEl.id === "footerNewsletterForm" ? "footer" : "modal_10_off";
          trackNewsletterSignup(signupLocation);

          emailEL.value = "";
          titleEl.innerText = "Welcome to the Pinhook Family";

          formWrapper.parentNode.classList.add("success");

          const inputs = formEl.querySelectorAll("input");
          setTimeout(() => {
            inputs.forEach((input) => (input.value = ""));
          }, 1000);

          document.dispatchEvent(
            new CustomEvent("newsletterSignupSuccess", {
              bubbles: true,
              detail: { email: data.email },
            }),
          );
        } else {
          showFormError(
            successMessageEl,
            errorMessageEl,
            response.error || "Something went wrong. Please try again.",
          );
        }
      });
    });
  }
}

/** Binds once: theme.js calls init() on load and again on DOMContentLoaded. */
let newsletterHandlersBound = false;

const newsletterInit = {
  init() {
    if (newsletterHandlersBound) return;
    if (!document.querySelector("[data-newsletter-form]")) return;

    newsletterHandlersBound = true;
    // NewsletterForm attaches to every [data-newsletter-form] in the document (modal, sections, etc.).
    // It must not depend on [data-newsletter-container], which collection pages often omit.
    new NewsletterForm(document.body);
  },
};

export default newsletterInit;
